import { useRef, useState } from 'react';
import { useDeletePhoto, useReorderPhotos, useUploadPhoto } from '../../garage/mutations';
import type { VehiclePhoto } from '../../garage/types';
import { Button, ConfirmDialog, Heading, Stack, Text, Thumbnail } from '../../ui';

/** Props for {@link PhotoManager}. */
export interface PhotoManagerProps {
  readonly vehicleId: string;
  readonly photos: readonly VehiclePhoto[];
  readonly vehicleName: string;
}

/**
 * Upload, order and remove a vehicle's photos.
 *
 * Ordering uses explicit buttons rather than drag-and-drop: the same capability then reaches
 * keyboard and screen-reader users without a second implementation, and the first photo being the
 * cover is stated rather than implied by position alone.
 *
 * @param props - Component props.
 * @returns The photo manager section.
 */
export function PhotoManager({ vehicleId, photos, vehicleName }: PhotoManagerProps): React.JSX.Element {
  const upload = useUploadPhoto(vehicleId);
  const remove = useDeletePhoto(vehicleId);
  const reorder = useReorderPhotos(vehicleId);

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function uploadFiles(files: FileList | null): Promise<void> {
    if (files === null || files.length === 0) {
      return;
    }

    setUploadError(null);

    // Sequential rather than parallel: one rejected file then reports its own reason instead of a
    // pile of simultaneous failures, and the gallery order stays predictable.
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync(file);
      } catch (cause) {
        setUploadError(cause instanceof Error ? cause.message : 'That photo could not be uploaded.');
        return;
      }
    }
  }

  function move(photoId: string, direction: -1 | 1): void {
    const order = photos.map((photo) => photo.id);
    const from = order.indexOf(photoId);
    const to = from + direction;

    if (from < 0 || to < 0 || to >= order.length) {
      return;
    }

    [order[from], order[to]] = [order[to] as string, order[from] as string];
    reorder.mutate(order);
  }

  function makeCover(photoId: string): void {
    reorder.mutate([photoId, ...photos.map((photo) => photo.id).filter((id) => id !== photoId)]);
  }

  return (
    <Stack gap="lg">
      <Heading level={2} size="lg">
        Photos
      </Heading>

      <div
        className={dragging ? 'gm-dropzone gm-dropzone--active' : 'gm-dropzone'}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          void uploadFiles(event.dataTransfer.files);
        }}
      >
        <Stack gap="sm" align="center">
          <Text muted>Drag photos here, or</Text>
          <Button variant="primary" onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
            {upload.isPending ? 'Uploading…' : 'Choose photos'}
          </Button>
          <Text size="sm" muted>
            Location data is removed from every photo before it is stored.
          </Text>
        </Stack>

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          hidden
          onChange={(event) => {
            void uploadFiles(event.target.files);
            event.target.value = '';
          }}
        />
      </div>

      {uploadError !== null && (
        <p className="gm-field-error" role="alert">
          {uploadError}
        </p>
      )}

      {photos.length > 0 && (
        <Stack direction="row" gap="lg" wrap>
          {photos.map((photo, index) => (
            <div key={photo.id} className="gm-photo-tile">
              <Thumbnail
                src={photo.thumbnailUrl}
                alt={`${vehicleName}, photo ${index + 1} of ${photos.length}`}
                size={140}
              />
              <Stack direction="row" gap="xs" wrap justify="center">
                <Button
                  onClick={() => move(photo.id, -1)}
                  disabled={index === 0}
                  aria-label={`Move photo ${index + 1} earlier`}
                >
                  ←
                </Button>
                <Button
                  onClick={() => move(photo.id, 1)}
                  disabled={index === photos.length - 1}
                  aria-label={`Move photo ${index + 1} later`}
                >
                  →
                </Button>
                <Button
                  onClick={() => makeCover(photo.id)}
                  disabled={index === 0}
                  aria-label={`Make photo ${index + 1} the cover`}
                >
                  Cover
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setPendingDelete(photo.id)}
                  aria-label={`Remove photo ${index + 1}`}
                >
                  Remove
                </Button>
              </Stack>
              {index === 0 && (
                <Text size="xs" muted>
                  Cover photo
                </Text>
              )}
            </div>
          ))}
        </Stack>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Remove this photo?"
        message="The image is deleted permanently. This cannot be undone."
        confirmLabel="Remove photo"
        destructive
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete !== null) {
            remove.mutate(pendingDelete);
          }

          setPendingDelete(null);
        }}
      />
    </Stack>
  );
}
